// FRONT-1728 — a reply the user pressed Stop on.
//
// A round already in flight when Stop lands cannot be aborted server-side, so
// it still finishes and persists its answer. `/history` marks that row
// `metadata.stopped` and sends only the parts the person had seen before
// pressing Stop — never what the round wrote afterwards, text or tool cards
// (a delegate card carries the real data it pulled). Both renderers show
// those seen parts as usual and put this note under them, or alone when
// nothing had been seen yet.
import type { UIMessage } from 'ai';
import { StopCircle } from '@phosphor-icons/react';

export const STOPPED_TURN_COPY = 'Stopped. You stopped this reply before it finished.';

/** True only for the server's explicit marker — anything else is a normal reply. */
export function isStoppedMessage(message: Pick<UIMessage, 'role'> & { metadata?: unknown }): boolean {
  if (message.role !== 'assistant') return false;
  const meta = message.metadata;
  return !!meta && typeof meta === 'object' && (meta as { stopped?: unknown }).stopped === true;
}

/**
 * The list with its last message marked stopped — when that message is the
 * assistant reply the person just stopped. Anything else (Stop before any
 * reply appeared, so the last message is still theirs) is returned as is.
 * Same array back when nothing changes, so a caller's state update is a no-op.
 */
export function markStoppedTail<T extends Pick<UIMessage, 'role'> & { metadata?: unknown }>(
  messages: T[],
): T[] {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant' || isStoppedMessage(last)) return messages;
  const meta = last.metadata && typeof last.metadata === 'object' ? last.metadata : {};
  return [...messages.slice(0, -1), { ...last, metadata: { ...meta, stopped: true } }];
}

export function StoppedTurnNote() {
  return (
    <div
      className="flex items-center gap-1.5 text-[length:var(--text-sm)] text-muted-foreground"
      data-testid="stopped-turn-note"
    >
      <StopCircle size={15} weight="regular" className="shrink-0" />
      <span className="text-pretty">{STOPPED_TURN_COPY}</span>
    </div>
  );
}
